import { Box, Stack, StackProps } from '@mui/material'
import { FC, useState, useId } from 'react'
import SidePanelRightArrow from '../../../../assets/images/right-pointing-arrow.svg?react'
import SidePanelLeftArrow from '../../../../assets/images/left-pointing-arrow.svg?react'

type SidePanelProps = React.PropsWithChildren<StackProps> & {
  /**
   * By default the side panel renders in an open state.
   *
   * This prop allows you to specify that the side panel should be closed when it first renders.
   *
   * @example
   * <SidePanel initialClosed>
   *   // side panel content here
   * </SidePanel>
   */
  initialClosed?: boolean

  /**
   * Controls the open/closed state externally (controlled mode).
   * When provided, internal state is ignored and this value drives open/closed.
   */
  isOpen?: boolean

  /**
   * Called when the open/closed state should change. Receives the new intended state.
   * Required when using controlled mode (`isOpen` prop).
   */
  onOpenChange?: (isOpen: boolean) => void

  /**
   * Add a prefix to all data-testid attributes in the side panel for easier querying in tests.
   *
   * - Root element: `${testIdPrefix}side-panel`
   * - Toggle arrow button: `${testIdPrefix}side-panel-toggle-arrow`
   * - Scroll container: `${testIdPrefix}side-panel-scroll-container`
   *
   * Defaults to an empty string.
   * @example
   * <SidePanel testIdPrefix="engagement-">
   *   // side panel content here
   * </SidePanel>
   * // In tests, you can then query elements like:
   * // getByTestId('engagement-side-panel')
   * // getByTestId('engagement-side-panel-toggle-arrow')
   * // getByTestId('engagement-side-panel-scroll-container')
   */
  testIdPrefix?: string
}

export const SidePanel: FC<SidePanelProps> = ({
  children,
  initialClosed = false,
  isOpen: controlledIsOpen,
  onOpenChange,
  testIdPrefix = '',
  ...stackProps
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(!initialClosed)
  const isControlled = controlledIsOpen !== undefined
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen
  const id = useId()
  const sidePanelId = `side-panel-${id}`

  const { sx: stackSx = {}, ...restStackProps } = stackProps

  const handleToggle = () => {
    if (isControlled) {
      onOpenChange?.(!isOpen)
    } else {
      setInternalIsOpen(prev => !prev)
    }
  }

  /**
   * When the side panel is closed, you can click anywhere on the side panel to open it.
   */
  const handleSidePanelClick = () => {
    if (!isOpen) {
      if (isControlled) {
        onOpenChange?.(true)
      } else {
        setInternalIsOpen(true)
      }
    }
  }

  return (
    <Stack
      direction="column"
      onClick={handleSidePanelClick}
      data-testid={`${testIdPrefix}side-panel`}
      sx={{
        width: isOpen ? '29.5rem' : '3.5rem',
        height: '100%',
        backgroundColor: 'var(--bg-page)',
        position: 'relative',
        transition: 'width 0.3s ease-in-out',
        flexShrink: 0,
        cursor: isOpen ? 'default' : 'pointer',
        minHeight: 0,
        boxShadow: `-0.1875rem 0.1875rem 0.25rem 0rem #00000020`,
        ...stackSx
      }}
      {...restStackProps}
    >
      <Box
        onClick={handleToggle}
        component={'button'}
        type="button"
        aria-label={isOpen ? 'Close side panel' : 'Open side panel'}
        aria-expanded={isOpen}
        aria-controls={sidePanelId}
        sx={{
          position: 'absolute',
          cursor: 'pointer',
          top: 'var(--spacing-s)',
          right: 'var(--spacing-s)',
          zIndex: 1,
          border: 'none',
          background: 'none',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        data-testid={`${testIdPrefix}side-panel-toggle-arrow`}
      >
        {isOpen ? <SidePanelRightArrow /> : <SidePanelLeftArrow />}
      </Box>

      <Stack
        id={sidePanelId}
        direction="column"
        aria-hidden={!isOpen}
        sx={{
          overflowY: 'auto',
          height: '100%',
          flex: 1,
          minHeight: 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          visibility: isOpen ? 'visible' : 'hidden'
        }}
        data-testid={`${testIdPrefix}side-panel-scroll-container`}
      >
        {children}
      </Stack>
    </Stack>
  )
}
